import { api } from "helpers/axios";
import { errorMessage, formatDateTime } from "helpers/common";
import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import ListPagination from "components/ui/ListPagination";

import { toast } from "helpers/toast";
import { useOutletContext } from "react-router-dom";
import { helpTextIcon } from "helpers/helpText";
import ErrorAlert from "components/ui/ErrorAlert";
import { useTranslation } from "translation/useTranslation";
import TableScrollbar from "components/EmployeeManagement/TableScrollbar";

function parseJsonString(value) {
  if (typeof value !== 'string') return { success: false };

  const trimmed = value.trim();
  if (!trimmed) return { success: false };

  try {
    const parsed = JSON.parse(trimmed);
    return { success: true, value: parsed };
  } catch {
    return { success: false };
  }
}

function normalizeJsonValue(value) {
  if (value === null || value === undefined) return value;

  if (typeof value === 'string') {
    const { success, value: parsed } = parseJsonString(value);
    if (success) {
      return normalizeJsonValue(parsed);
    }
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((item) => normalizeJsonValue(item));
  }

  if (typeof value === 'object') {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = normalizeJsonValue(value[key]);
      return acc;
    }, {});
  }

  return value;
}

function formatJsonBlock(value) {
  if (value === null || value === undefined) return '';

  try {
    const normalized = normalizeJsonValue(value);
    return JSON.stringify(normalized, null, 2);
  } catch {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
}

function LogRow({ log }) {
  const { editableTranslate } = useTranslation();

  const [showDetails, setShowDetails] = useState(false);

  const renderJsonSection = (label, value) => (
    <>
      <span className="d-block font-weight-bold mt-2">{label}</span>
      <div className="audit-log-json-container bg-light border rounded p-2" style={{ maxHeight: '250px', overflow: 'auto' }}>
        <pre className="mb-0">{formatJsonBlock(value)}</pre>
      </div>
    </>
  );

  const handleClickDetails = (e) => {
    e?.currentTarget?.blur();
    setShowDetails(!showDetails);
  };

  return <Fragment>
    <tr className={`cursor-pointer ${showDetails ? 'selected-table-row' : 'table-row'}`} onClick={handleClickDetails}>
      <td className="text-center v-middle">
        <i className={`open-row-icon bi bi-chevron-${showDetails ? 'up' : 'down'}`}></i>
      </td>
      <td>{formatDateTime(log.created_at)}</td>
      <td>{log.causer?.name}</td>
      <td>{log.log}</td>
    </tr>
    {
      showDetails && <tr>
        <td></td>
        <td colSpan={3}>
          <div className="audit-log-details">
            {renderJsonSection(editableTranslate('audit_logs.new_values'), log.new_values)}
            {renderJsonSection(editableTranslate('audit_logs.old_values'), log.old_values)}
          </div>
        </td>
      </tr>
    }
  </Fragment>;
}

export default function AuditLogs() {
  const { translate, editableTranslate } = useTranslation();

  const { helps } = useOutletContext();

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState(null);

  const [evaluatorEnabled, setEvaluatorEnabled] = useState(null);

  const [logs, setLogs] = useState([]);

  const [pagination, setPagination] = useState({
    page: 1,
    totalRows: 0,
    totalPages: 1,
    perPage: 20
  });

  const tableRef = useRef(null);
  const theadRef = useRef(null);

  const [resizedCount, setResizedCount] = useState(0);

  const auditLogHelp = useMemo(() => {
    if (helps) {
      return helps.filter(h => h.key === 'audit-logs-intro')[0] || null;
    }

    return null;
  }, [helps]);

  const fetchEvaluatorEnabled = async () => {
    setError(null);

    try {
      const { data, status } = await api.get('/audit-logs/evaluator-enabled');
      if (status === 200) {
        setEvaluatorEnabled(data.data.enabled);
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch audit logs evaluator enabled'));
      Sentry.captureException(err);
    }
  };

  const fetchAuditLogs = async (page = null) => {
    if (!page) page = pagination.page;

    setError(null);
    setLoading(true);

    try {
      const { data, status } = await api.get(`/audit-logs?limit=${pagination.perPage}&page=${page}`);
      if (status === 200) {
        setLogs(data.data);
        const temp = data.meta.pagination;
        setPagination({ page, totalRows: +temp.total, totalPages: temp.total_pages, perPage: +temp.per_page });
      }
    } catch (err) {
      setError(errorMessage(err, 'fetch audit logs'));
      Sentry.captureException(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchEvaluatorEnabled();
  }, []);

  useEffect(() => {
    fetchAuditLogs();
  }, [pagination.page, pagination.perPage]);

  useEffect(() => {
    setResizedCount(c => c + 1);

    const theadEl = theadRef.current;
    if (!theadEl) return;

    const handleScrollTable = () => {
      const navHeight = document.getElementById('navbar-menu')?.offsetHeight || 0;

      const theadParentCoordinates = theadEl.parentElement.getBoundingClientRect();
      if (theadParentCoordinates.y < navHeight) {
        theadEl.style.transform = `translate3d(0, ${-theadParentCoordinates.y + navHeight}px, 0)`;
      } else {
        theadEl.style.transform = 'translate3d(0,0,0)';
      }
    };
    window.addEventListener('scroll', handleScrollTable);
    return () => window.removeEventListener('scroll', handleScrollTable);
  }, [logs]);

  const handleEvaluatorChange = (e) => {
    const val = (e.target.value === 'yes' ? true : false);

    const previous = evaluatorEnabled;
    setEvaluatorEnabled(val);
    saveEvaluatorEnabled(val, previous);
  };

  const saveEvaluatorEnabled = async (enabled, previous) => {
    setError(null);

    try {
      const { status } = await api.patch(`/audit-logs/evaluator-enabled`, { enabled });
      if (status === 200) {
        toast.success(translate('form.success.saved'));

        // on another page the page effect loads page 1, a direct fetch would load it twice
        if (pagination.page === 1) fetchAuditLogs(1);
        else setPagination(p => ({ ...p, page: 1 }));
      }
    } catch (err) {
      setEvaluatorEnabled(previous);
      setError(errorMessage(err, 'save audit logs evaluator enabled'));
      Sentry.captureException(err);
    }
  };

  return (
    <>
      <div className="col-lg col-md-9 section-col-right d-flex">
        <div className={`bg-white w-100 p-4 content-card`}>
          <h4>{editableTranslate('global.menu.settings.audit_logs')}</h4>
          <p className="section-description">
            {editableTranslate("global.menu.settings.audit_logs.description")}
            {
              auditLogHelp ? helpTextIcon(auditLogHelp?.key, auditLogHelp?.short_text) : ''
            }
          </p>

          <div className="mt-3">
            {
              error &&
              <div className="mt-3">
                <ErrorAlert error={error} onClose={() => setError(null)} />
              </div>
            }

            <div className="gray-box filters-box">
              <label className="question-label font-weight-bold">{editableTranslate('audit_logs.evaluator_label')}</label>
              <div className="pl-1-2 py-2">
                <div className="form-group mb-2">
                  <div className="form-check form-check-inline mr-5">
                    <input className="form-check-input" type="radio" id="audit_logs_evaluator_yes" name="audit_logs_evaluator" value="yes" onChange={handleEvaluatorChange} checked={evaluatorEnabled !== null && evaluatorEnabled ? true : false} data-testid="audit-logs-evaluator-yes-yes-radio" />
                    <label className="form-check-label" htmlFor="audit_logs_evaluator_yes">{editableTranslate('audit_logs.evaluator_enabled_yes')}</label>
                  </div>
                  <div className="form-check form-check-inline">
                    <input className="form-check-input" type="radio" id="audit_logs_evaluator_no" name="audit_logs_evaluator" value="no" onChange={handleEvaluatorChange} checked={evaluatorEnabled !== null && !evaluatorEnabled ? true : false} data-testid="audit-logs-evaluator-no-no-radio" />
                    <label className="form-check-label" htmlFor="audit_logs_evaluator_no">{editableTranslate('form.no')}</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-4">
              <div className="table-responsive has-custom-scrollbar">
                <table ref={tableRef} className="table common-table" data-testid="logs-table">
                  <thead ref={theadRef} className="sticky-top">
                    <tr>
                      <th></th>
                      <th>{editableTranslate('audit_logs.time')}</th>
                      <th>{editableTranslate('audit_logs.user')}</th>
                      <th>{editableTranslate('audit_logs.summary')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {
                      !loading && logs.map((log, i) => <LogRow key={pagination.page + '-' + i} log={log} />)
                    }
                    {
                      loading && <tr>
                        <td colSpan={4}>
                          <div className="d-flex justify-content-center" data-testid="loading">
                            <div className="spinner-border" role="status">
                              <span className="sr-only">Wird geladen...</span>
                            </div>
                          </div>
                        </td>
                      </tr>
                    }
                    {
                      !loading && logs.length === 0 && <tr>
                        <td colSpan={4} className="text-center" data-testid="empty-state">{editableTranslate('global.no_data')}</td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
              <TableScrollbar tableRef={tableRef} data={logs} trigger={resizedCount} />

              <ListPagination
                page={pagination.page}
                perPage={pagination.perPage}
                totalPages={pagination.totalPages}
                totalRows={pagination.totalRows}
                setPage={(page) => setPagination({ ...pagination, page })}
                setPerPage={(perPage) => setPagination({ ...pagination, page: 1, perPage })}
                className="mt-4"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
